import {
	setAdvisoriesLoading,
	setAdvisoriesObject
} from 'slice-reducers/locationAndAdvisoriesSlice';
import { fetchMapLayerSourceData, getAdvisories } from 'apis/map.api';
import { createPointFeature, getSelectedMapLayerList, updateLayerSettings } from 'utils/mapUtils';
import { setUserLocation, setUserLocationLoading } from 'slice-reducers/appSlice';
import { geolocateUser } from 'actions/geolocation';
import { logEvent } from 'actions/firebase';
import { PIN_DROP_GPS } from 'constants/firebase';
import { notifyAndFlySourceId } from 'constants/mapConstants';
import { addCoordinatesToUrl } from 'actions/url';
import {
	mapSourcesSelector,
	setMapLayers,
	setMapLoading,
	setMapSources,
	setMapBasemaps,
	setMapModifiers,
	setMapSections,
	setSelectedBasemap,
	setMapRegions,
	setVisibleMapLayers,
	setGeoJsonData
} from 'slice-reducers/mapSlice';
import {
	getSelectedMapLayersFromLS,
	saveSelectedMapLayersToLS,
	saveSelectedBasemapToLS
} from 'actions/localStorage';
import { getMapInfo } from 'apis/map.api';
import { SELECTED_BASEMAP } from 'constants/localStorageKeys';
import axios from 'axios';
import axiosInstance from 'services/axiosConfig';

export const fetchAndSetAdvisories =
	({ lat, long, showLoader = true }) =>
	async dispatch => {
		if (showLoader) {
			dispatch(setAdvisoriesLoading(true));
		}

		const res = await dispatch(getAdvisories({ lat, long }));
		dispatch(setAdvisoriesObject(res));
		if (showLoader) {
			dispatch(setAdvisoriesLoading(false));
		}
	};

export const fetchAndSetUserLocation =
	({ navigate, lat, long, location }) =>
	async dispatch => {
		dispatch(setUserLocationLoading(true));
		const userLocation = await dispatch(geolocateUser());
		if (userLocation) {
			const location = createPointFeature({
				lat: userLocation.lat,
				long: userLocation.long,
				place_name: `LAT: ${Number(userLocation.lat).toFixed(6)} || LONG: ${Number(
					userLocation.long
				).toFixed(6)}`,
				optionType: 'userLocation'
			});
			dispatch(setUserLocation(location));
			dispatch(setUserLocationLoading(false));
			if (!lat || !long) {
				//Set marker and url params to user location if another location is not already chosen
				logEvent(PIN_DROP_GPS);
				addCoordinatesToUrl({
					lat: userLocation.lat,
					long: userLocation.long,
					navigate,
					pathname: location.pathname
				});
			}
		}
	};

export const fetchNotifyAndFlyLayer =
	({ map }) =>
	async (dispatch, getState) => {
		const sources = mapSourcesSelector(getState());
		const notifyAndFlySource = sources.find(source => source.handle === notifyAndFlySourceId);
		const notifyAndFlyLayerUrl = notifyAndFlySource?.source_url;
		if (notifyAndFlyLayerUrl) {
			const updatedNotifyAndFlyData = await dispatch(fetchMapLayerSourceData(notifyAndFlyLayerUrl));
			const currentLayer = map.getSource(notifyAndFlySourceId);
			if (currentLayer) {
				currentLayer.setData(updatedNotifyAndFlyData);
			}
		}
	};

export const fetchNotifyLayerAndAdvisories =
	({ map, lat, long, showLoader }) =>
	async dispatch => {
		await dispatch(fetchNotifyAndFlyLayer({ map }));
		//Do not show loading state, just update UI quietly
		if (lat && long) {
			await dispatch(fetchAndSetAdvisories({ lat, long, showLoader }));
		}
	};

export const getMapData = region => async dispatch => {
	dispatch(setMapLoading(true));
	const res = await dispatch(getMapInfo());
	const { sources, sections, layers, base_maps, modifiers, regions } = res;
	dispatch(setMapSources(sources));
	dispatch(setMapSections(sections));
	dispatch(setMapLayers(layers));
	dispatch(setMapBasemaps(base_maps));
	if (!localStorage.getItem(SELECTED_BASEMAP)) {
		const defaultBasemap = base_maps.find(b => b.is_default)?.id;
		dispatch(setSelectedBasemap(defaultBasemap));
		saveSelectedBasemapToLS(defaultBasemap);
	}
	dispatch(setMapModifiers(modifiers));
	dispatch(setMapRegions(regions));

	//Set initial map layer settings in local storage
	const localStorageSelectedMapLayers = getSelectedMapLayersFromLS();
	const updatedMapLayerSettings = updateLayerSettings({
		layerToggleList: layers,
		currentSettings: localStorageSelectedMapLayers,
		modifierToggleList: modifiers
	});
	saveSelectedMapLayersToLS(updatedMapLayerSettings);

	//Set initial visible map layers in redux
	const visibleMapLayers = getSelectedMapLayerList({
		allLayers: layers,
		currentSettings: updatedMapLayerSettings,
		allModifiers: modifiers
	});
	dispatch(setVisibleMapLayers(visibleMapLayers));
	dispatch(setMapLoading(false));
	return { visibleMapLayers };
};

export const fetchGeoJsonData = geoJsonSources => async (dispatch, getState) => {
	// Fetch data for each GeoJSON source
	const geoJsonData = {
		...getState().map.geoJsonData //Keep existing data and only update sources that are being polled
	};
	for (const source of geoJsonSources) {
		try {
			if (source.host === 'esri') {
				const url = `${source.source_url}/query?where=1=1&outFields=*&f=geojson&outSR=4326`;
				const response = await axios.get(url);
				geoJsonData[source.handle] = response.data;
			} else {
				const response = await axiosInstance.get(source.source_url);
				geoJsonData[source.handle] = response.data;
			}
		} catch (error) {
			console.error(`Error fetching GeoJSON data for ${source.handle}:`, error);
		}
	}

	// Batch the updates together
	dispatch(setGeoJsonData(geoJsonData));
};

export const startGeoJsonPolling = geoJsonSources => dispatch => {
	// Clear any existing interval
	if (window.geoJsonPollingInterval) {
		clearInterval(window.geoJsonPollingInterval);
		window.geoJsonPollingInterval = null;
	}

	// Initial fetch
	dispatch(fetchGeoJsonData(geoJsonSources));

	const sourceHandlesToPoll = ['tfrs'];

	const sourcesToPoll = geoJsonSources.filter(source =>
		sourceHandlesToPoll.includes(source.handle)
	);

	// Set up polling every 5 minutes
	window.geoJsonPollingInterval = setInterval(() => {
		dispatch(fetchGeoJsonData(sourcesToPoll));
	}, 300000);

	// Return cleanup function
	return () => {
		if (window.geoJsonPollingInterval) {
			clearInterval(window.geoJsonPollingInterval);
			window.geoJsonPollingInterval = null;
		}
	};
};
